/**
 * Column width bounds — ONE constant for every surface that touches a column
 * width (SCH-2162 KTD9).
 *
 * `@blocknote/xl-multi-column` stores a column's width as a unitless flex-grow
 * weight: `1` is an equal share, and a 70/30 layout is two columns whose weights
 * sit in that ratio. Four places must agree on which weights are legal, and each
 * imports this module rather than restating the numbers:
 *
 *   - the blog and docs vocabularies (`blog-post.ts`, `docs-content.ts`), which
 *     reject an out-of-bounds width at the API;
 *   - the web render layer (`apps/web/src/lib/sanitize/column-width.ts`), which
 *     restores a width from the stored `data-width` attribute only when it is in
 *     bounds;
 *   - the editor column guard, which clamps a width into bounds as it enters the
 *     document, so a paste cannot produce a save the API refuses.
 *
 * WHY THESE BOUNDS, read from xl-multi-column 0.52.1 rather than assumed:
 *
 *   - the resize handle floors a width at 0.5 and has NO ceiling;
 *   - adding a column rescales every width, which can take one below 0.5;
 *   - paste accepts any finite number.
 *
 * So no bound here matches something the UI enforces: the editor clamp is what
 * keeps authored widths inside them. The 0.01 floor also keeps
 * `Number#toString` — which is how BlockNote writes `data-width` — out of
 * exponent form, so the render layer's whole-string number check never meets
 * `1e-7`. The 100 ceiling is a 10,000:1 ratio against the floor, far past any
 * layout an author would want.
 *
 * Rejected, never coerced, at the API: that is the vocabularies' standing rule.
 */

export const COLUMN_WIDTH_MIN = 0.01
export const COLUMN_WIDTH_MAX = 100

/** True for a finite number inside the shared bounds, inclusive. */
export function isColumnWidth(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= COLUMN_WIDTH_MIN && value <= COLUMN_WIDTH_MAX
}

export const COLUMN_WIDTH_MESSAGE = `Column width must be a finite number from ${COLUMN_WIDTH_MIN} to ${COLUMN_WIDTH_MAX}`
